import React, { useEffect, useMemo, useState } from "react";
import {
  DataGrid,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
} from "@mui/x-data-grid";
import { Box, Button, Grid, MenuItem, TextField } from "@mui/material";
import Modal from "@mui/material/Modal";
import SaveAlt from "@mui/icons-material/SaveAlt";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { toast } from "react-toastify";
import ApiDatePickerField from "../../../components/date/ApiDatePickerField";

import "./AdminComponents.scss";
import {
  resolveServiceGroupForRole,
  filterClassificationsForGroup,
  DIAGNOSTIC_DOCTOR_ROLES,
  CLASSIFICATION_ALL,
  roleLabel,
} from "../api/diagnosticDoctorApi";
import {
  fetchDiagnosticUsersFromList,
  editDiagnosticUserViaEditUser,
  deleteDiagnosticUser,
} from "../api/userManagementDiagnosticApi";
import {
  normalizeSalutation,
  salutationSelectOptions,
} from "../utils/diagnosticUserFormShared";
import {
  dataGridSx,
  modalBoxSx,
  confirmButtonSx,
  cancelButtonSx,
} from "./bedMasterStyles";

const GENDERS = [
  { value: "M", label: "Male" },
  { value: "F", label: "Female" },
  { value: "T", label: "Transgender" },
];

const dateForInput = (value) => (value ? String(value).split("T")[0] : "");

const splitName = (row = {}) => {
  const first = String(row.first_name ?? row.firstName ?? "").trim();
  const last = String(row.last_name ?? row.lastName ?? "").trim();
  if (first || last) {
    return { first_name: first, last_name: last };
  }
  const fullName = String(row.name ?? "").trim();
  if (!fullName) {
    return { first_name: "", last_name: "" };
  }
  const parts = fullName.split(/\s+/).filter(Boolean);
  return {
    first_name: parts[0] || "",
    last_name: parts.slice(1).join(" "),
  };
};

const classificationSelectValue = (id) =>
  id == null || id === "" ? CLASSIFICATION_ALL : String(id);

function DiagnosticDoctorList({ refreshList, lookups = { groups: [], classifications: [] } }) {
  const [doctors, setDoctors] = useState([]);
  const [roleFilter, setRoleFilter] = useState("");
  const [doctorToDelete, setDoctorToDelete] = useState(null);
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const fetchDoctors = () => {
    fetchDiagnosticUsersFromList({ roleFilter: roleFilter || undefined })
      .then((list) =>
        setDoctors(
          list.map((item) => {
            const nameParts = splitName(item);
            return {
            id: item.id,
            diagnostic_doctor_id: item.diagnostic_doctor_id,
            user_type: item.user_type || "",
            role_type: String(
              item.role_type || item.user_type || item.role || "",
            ).toLowerCase(),
            username: item.username ?? "",
            first_name: item.first_name ?? nameParts.first_name,
            last_name: item.last_name ?? nameParts.last_name,
            name:
              item.name ||
              [item.first_name, item.last_name].filter(Boolean).join(" ") ||
              [nameParts.first_name, nameParts.last_name].filter(Boolean).join(" "),
            salutation: normalizeSalutation(item.salutation ?? ""),
            email: item.email ?? "",
            phone_no: item.phone_no ?? "",
            registration_no: item.registration_no ?? "",
            specialization: item.specialization ?? "",
            qualifications: item.qualifications ?? "",
            gender: item.gender ?? "",
            dob: dateForInput(item.dob),
            city: item.city ?? "",
            state: item.state ?? "",
            district: item.district ?? "",
            date_joined: dateForInput(item.date_joined),
            active: item.active !== undefined ? item.active : true,
            service_group_id: item.service_group_id ?? "",
            service_classification_id: item.service_classification_id,
            service_group_name: item.service_group_name ?? "",
            service_classification_name: item.service_classification_name ?? "",
          };
          })
        )
      )
      .catch((err) => {
        console.error("fetchDiagnosticUsersFromList:", err);
        toast.error(err?.message || "Could not load diagnostic doctors");
      });
  };

  useEffect(() => {
    fetchDoctors();
  }, [refreshList, roleFilter]);

  const editGroupClassifications = useMemo(() => {
    if (!editingDoctor?.service_group_id) return [];
    return filterClassificationsForGroup(
      lookups.classifications,
      Number(editingDoctor.service_group_id)
    );
  }, [editingDoctor?.service_group_id, lookups.classifications]);

  const groupName = (id) => {
    const match = lookups.groups.find((g) => Number(g.id) === Number(id));
    return match ? match.name : id || "-";
  };

  const classificationName = (row) => {
    if (row.service_classification_id == null || row.service_classification_id === "") {
      return "All";
    }
    if (row.service_classification_name) return row.service_classification_name;
    const match = lookups.classifications.find(
      (c) => Number(c.id) === Number(row.service_classification_id)
    );
    return match ? match.name : row.service_classification_id;
  };

  const handleEditOpen = (row) => {
    const parts = splitName(row);
    setEditingDoctor({
      ...row,
      ...parts,
      salutation: normalizeSalutation(row.salutation),
      service_group_id: row.service_group_id ? String(row.service_group_id) : "",
      service_classification_id: classificationSelectValue(
        row.service_classification_id
      ),
      password: "",
      hasPassword: true,
      dob: dateForInput(row.dob) || "",
      date_joined: dateForInput(row.date_joined) || "",
    });
  };

  const handleConfirmDelete = () => {
    const userId = doctorToDelete.id;
    if (!userId || userId === "-") {
      toast.error("User id is missing");
      setDoctorToDelete(null);
      return;
    }

    deleteDiagnosticUser(userId)
      .then((res) => {
        if (res?.status === "Success") {
          toast.success(res?.message || "Diagnostic doctor deleted");
          setDoctors((rows) => rows.filter((row) => row.id !== userId));
        } else {
          toast.error(res?.message || "Could not delete diagnostic doctor");
        }
      })
      .catch((err) => {
        console.error("deleteDiagnosticUser:", err);
        toast.error(
          err?.response?.data?.message || "Could not delete diagnostic doctor",
        );
      })
      .finally(() => setDoctorToDelete(null));
  };

  const handleEditSubmit = () => {
    if (!String(editingDoctor.first_name || "").trim()) {
      toast.error("First name is required");
      return;
    }
    if (!editingDoctor.service_group_id) {
      toast.error("Service Group is required");
      return;
    }
    if (!String(editingDoctor.gender || "").trim()) {
      toast.error("Gender is required");
      return;
    }

    setIsSaving(true);

    editDiagnosticUserViaEditUser({
      ...editingDoctor,
      password: String(editingDoctor.password || "").trim() || undefined,
    })
      .then((res) => {
        if (res?.status === "Success") {
          toast.success(res?.message || "Diagnostic doctor updated");
          setEditingDoctor(null);
          fetchDoctors();
        } else {
          toast.error(
            res?.error || res?.message || "Could not update diagnostic doctor",
          );
        }
      })
      .catch((err) => {
        console.error("editDiagnosticUserViaEditUser:", err);
        toast.error(
          err?.response?.data?.message || "Could not update diagnostic doctor",
        );
      })
      .finally(() => setIsSaving(false));
  };

  const dashIfEmpty = (params) => params?.value || "-";

  const columns = [
    {
      field: "name",
      headerName: "Name",
      width: 180,
      valueFormatter: dashIfEmpty,
    },
    {
      field: "username",
      headerName: "User Name",
      width: 130,
      valueFormatter: dashIfEmpty,
    },
    {
      field: "role_type",
      headerName: "Role",
      width: 130,
      valueFormatter: (params) => roleLabel(params?.value),
    },
    {
      field: "service_group_id",
      headerName: "Service Group",
      width: 160,
      valueFormatter: (params) => groupName(params?.value),
    },
    {
      field: "service_classification_id",
      headerName: "Classification",
      width: 160,
      valueGetter: (params) => classificationName(params.row),
    },
    {
      field: "email",
      headerName: "Email",
      width: 200,
      valueFormatter: dashIfEmpty,
    },
    {
      field: "phone_no",
      headerName: "Phone No",
      width: 130,
      valueFormatter: dashIfEmpty,
    },
    {
      field: "specialization",
      headerName: "Specialization",
      width: 150,
      valueFormatter: dashIfEmpty,
    },
    {
      field: "active",
      headerName: "Status",
      width: 100,
      renderCell: (params) => (
        <div
          style={{ color: params.value ? "green" : "red", fontWeight: "bold" }}
        >
          {params.value ? "Active" : "Inactive"}
        </div>
      ),
    },
    {
      field: "action",
      headerName: "Action",
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <>
          <button
            onClick={() => handleEditOpen(params.row)}
            style={{ color: "blue", fontWeight: "bold", marginRight: "8px" }}
          >
            Edit
          </button>
          <button
            onClick={() => setDoctorToDelete(params.row)}
            style={{ color: "red", fontWeight: "bold" }}
          >
            Delete
          </button>
        </>
      ),
    },
  ];

  const generatePdf = () => {
    const doc = new jsPDF();
    doc.autoTable({
      head: [
        [
          "Name",
          "Role",
          "Service Group",
          "Classification",
          "Email",
          "Phone",
          "Status",
        ],
      ],
      body: doctors.map((d) => [
        d.name || "-",
        roleLabel(d.role_type),
        groupName(d.service_group_id),
        classificationName(d),
        d.email || "-",
        d.phone_no || "-",
        d.active ? "Active" : "Inactive",
      ]),
    });
    doc.save("Diagnostic_Doctor_List.pdf");
  };

  const generateCsvData = () =>
    doctors.map((d) => ({
      Name: d.name || "-",
      "User Name": d.username || "-",
      Role: roleLabel(d.role_type),
      "Service Group": groupName(d.service_group_id),
      Classification: classificationName(d),
      Email: d.email || "-",
      "Phone No": d.phone_no || "-",
      Specialization: d.specialization || "-",
      Status: d.active ? "Active" : "Inactive",
    }));

  const editField = (key, label, extra = {}) => (
    <Grid item xs={12} sm={6}>
      <TextField
        label={label}
        value={editingDoctor[key] ?? ""}
        onChange={(e) =>
          setEditingDoctor({ ...editingDoctor, [key]: e.target.value })
        }
        fullWidth
        {...extra}
      />
    </Grid>
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        margin: "auto",
        width: "100%",
        maxWidth: "1200px",
        padding: "1rem",
      }}
    >
      <div style={{ width: "100%" }}>
        <h4 style={{ textAlign: "center" }}>Diagnostic Doctor List</h4>

        <Box sx={{ mb: 2, maxWidth: 280 }}>
          <TextField
            select
            label="Filter by Role"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            fullWidth
            size="small"
          >
            <MenuItem value="">All</MenuItem>
            {DIAGNOSTIC_DOCTOR_ROLES.map((role) => (
              <MenuItem key={role.value} value={role.value}>
                {role.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <div className="display-flex">
          <DataGrid
            rows={doctors}
            columns={columns}
            getRowId={(row) =>
              row.username || row.diagnostic_doctor_id || row.id
            }
            components={{
              Toolbar: () => (
                <Grid
                  container
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 2 }}
                >
                  <Grid item xs={12} sm={6} md={8} sx={{ display: "flex", gap: 1 }}>
                    <GridToolbarColumnsButton />
                    <GridToolbarFilterButton />
                    <GridToolbarDensitySelector />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    sx={{
                      display: "flex",
                      gap: 1,
                      justifyContent: { xs: "center", sm: "flex-end" },
                    }}
                  >
                    <Button
                      variant="text"
                      color="primary"
                      onClick={generatePdf}
                      startIcon={<SaveAlt />}
                    >
                      Download PDF
                    </Button>
                    <CSVLink
                      data={generateCsvData()}
                      filename="diagnostic_doctor_list.csv"
                      target="_blank"
                    >
                      <Button variant="text" color="primary" startIcon={<SaveAlt />}>
                        Download CSV
                      </Button>
                    </CSVLink>
                  </Grid>
                </Grid>
              ),
            }}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 10 } },
            }}
            pageSizeOptions={[5, 10]}
            getRowClassName={() => "custom-row"}
            sx={dataGridSx}
          />
        </div>
      </div>

      <Modal
        open={!!doctorToDelete}
        onClose={() => setDoctorToDelete(null)}
        style={{
          backdropFilter: "blur(5px)",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        }}
      >
        <Box sx={modalBoxSx}>
          <h3 style={{ marginTop: 0, fontSize: "32px" }}>Confirm Deletion</h3>
          <p style={{ fontSize: "20px" }}>
            Are you sure you want to delete diagnostic doctor{" "}
            <b>{doctorToDelete?.name || doctorToDelete?.diagnostic_doctor_id}</b>?
            This also removes their login user.
          </p>
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: "10%" }}>
            <Button onClick={() => setDoctorToDelete(null)} sx={cancelButtonSx}>
              CANCEL
            </Button>
            <Button onClick={handleConfirmDelete} sx={confirmButtonSx}>
              CONFIRM DELETION
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={!!editingDoctor}
        onClose={() => setEditingDoctor(null)}
        style={{
          backdropFilter: "blur(5px)",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        }}
      >
        <Box sx={{ ...modalBoxSx, maxHeight: "90vh", overflowY: "auto" }}>
          <h3 style={{ marginTop: 0 }}>Edit Diagnostic Doctor</h3>
          {editingDoctor && (
            <form onSubmit={(e) => e.preventDefault()}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Role"
                    value={editingDoctor.role_type ?? ""}
                    onChange={(e) => {
                      const nextRole = e.target.value;
                      const matched = resolveServiceGroupForRole(
                        nextRole,
                        lookups.groups
                      );
                      setEditingDoctor({
                        ...editingDoctor,
                        role_type: nextRole,
                        service_group_id: matched?.id
                          ? String(matched.id)
                          : "",
                        service_classification_id: CLASSIFICATION_ALL,
                      });
                    }}
                    fullWidth
                  >
                    {DIAGNOSTIC_DOCTOR_ROLES.map((role) => (
                      <MenuItem key={role.value} value={role.value}>
                        {role.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Salutation"
                    value={editingDoctor.salutation ?? ""}
                    onChange={(e) =>
                      setEditingDoctor({
                        ...editingDoctor,
                        salutation: e.target.value,
                      })
                    }
                    fullWidth
                  >
                    {salutationSelectOptions(editingDoctor.salutation).map(
                      (title) => (
                        <MenuItem key={title} value={title}>
                          {title}
                        </MenuItem>
                      ),
                    )}
                  </TextField>
                </Grid>
                {editField("first_name", "First Name")}
                {editField("last_name", "Last Name")}
                {editField("email", "Email", { type: "email" })}
                {editField("phone_no", "Phone No")}
                {editField("password", "Password", {
                  type: "password",
                  placeholder: editingDoctor.hasPassword
                    ? "Leave blank to keep current password"
                    : "Enter new password",
                  helperText: editingDoctor.hasPassword
                    ? "Password is already set for this account."
                    : "",
                  autoComplete: "new-password",
                  inputProps: {
                    autoComplete: "new-password",
                    "data-lpignore": "true",
                    "data-1p-ignore": "true",
                  },
                })}
                {editField("registration_no", "Registration No")}
                {editField("specialization", "Specialization")}
                {editField("qualifications", "Qualifications")}
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Service Group"
                    value={editingDoctor.service_group_id ?? ""}
                    onChange={(e) =>
                      setEditingDoctor({
                        ...editingDoctor,
                        service_group_id: e.target.value,
                        service_classification_id: CLASSIFICATION_ALL,
                      })
                    }
                    fullWidth
                  >
                    <MenuItem value="">Select Service Group</MenuItem>
                    {lookups.groups.map((group) => (
                      <MenuItem key={group.id} value={String(group.id)}>
                        {group.name} ({group.code})
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Service Classification"
                    value={editingDoctor.service_classification_id ?? CLASSIFICATION_ALL}
                    onChange={(e) =>
                      setEditingDoctor({
                        ...editingDoctor,
                        service_classification_id: e.target.value,
                      })
                    }
                    fullWidth
                    disabled={!editingDoctor.service_group_id}
                  >
                    <MenuItem value={CLASSIFICATION_ALL}>All</MenuItem>
                    {editGroupClassifications.map((item) => (
                      <MenuItem key={item.id} value={String(item.id)}>
                        {item.name} ({item.code})
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    required
                    label="Gender"
                    value={editingDoctor.gender ?? ""}
                    onChange={(e) =>
                      setEditingDoctor({
                        ...editingDoctor,
                        gender: e.target.value,
                      })
                    }
                    fullWidth
                  >
                    {GENDERS.map((g) => (
                      <MenuItem key={g.value} value={g.value}>
                        {g.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <label style={{ display: "block", marginBottom: "8px" }}>
                    Date of Birth
                  </label>
                  <ApiDatePickerField
                    id="edit-diagnostic-dob"
                    value={editingDoctor.dob || ""}
                    onChange={(next) =>
                      setEditingDoctor({ ...editingDoctor, dob: next })
                    }
                    className="form-control"
                    slotProps={{
                      popper: { sx: { zIndex: 9999 } },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <label style={{ display: "block", marginBottom: "8px" }}>
                    Date Joined
                  </label>
                  <ApiDatePickerField
                    id="edit-diagnostic-date-joined"
                    value={editingDoctor.date_joined || ""}
                    onChange={(next) =>
                      setEditingDoctor({ ...editingDoctor, date_joined: next })
                    }
                    className="form-control"
                    slotProps={{
                      popper: { sx: { zIndex: 9999 } },
                    }}
                  />
                </Grid>
                {editField("city", "City")}
                {editField("district", "District")}
                {editField("state", "State")}
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Status"
                    value={editingDoctor.active ? "true" : "false"}
                    onChange={(e) =>
                      setEditingDoctor({
                        ...editingDoctor,
                        active: e.target.value === "true",
                      })
                    }
                    fullWidth
                  >
                    <MenuItem value="true">Active</MenuItem>
                    <MenuItem value="false">Inactive</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
              <Button
                type="submit"
                disabled={isSaving}
                onClick={handleEditSubmit}
                variant="contained"
                style={{
                  backgroundColor: "#213360",
                  color: "#fff",
                  borderRadius: "20px",
                  marginTop: "20px",
                }}
              >
                Save Changes
              </Button>
            </form>
          )}
        </Box>
      </Modal>
    </div>
  );
}

export default DiagnosticDoctorList;
